/**
 * PROJECT-111A T0808 — the background run: status, kernel diffs, accept
 * =====================================================================
 *
 * The user accepted some marked spans and already has their new output. This is the
 * other half arriving afterwards: `62` picked a kernel per mark, `73` proposed a change
 * to it, and **none of it is in force** (D-017).
 *
 * Three surfaces, all of them wiring
 * ----------------------------------
 * D-016 decided that none of this justifies a new component:
 *   - "the chain fired"  → one `patchRunId` and a status line
 *   - "what changed"     → `UnifiedDiffViewer`, already the kernel version differ
 *   - "make it real"     → `applyKernelPatch`, already the guarded write path
 * If this file ever grows a differ of its own, D-016 was wrong and should be re-opened
 * rather than worked around.
 *
 * Why the accept button lives here
 * --------------------------------
 * `73` rewrites the INSTRUCTION rather than appending to it, unattended, across several
 * kernels. R-01's 22 `poisoned_shared` rows came from exactly that with nothing checking.
 * So the run stops at a proposal and this list is where a human commits it — through
 * `/kernel/apply-patch`, the one path `guard_kernel_write` actually runs on. Its 409 is a
 * question ("this touches N bots, confirm?"), not a failure, and is rendered as one.
 *
 * Each proposal is accepted or skipped INDIVIDUALLY. That is the point of D-015's
 * per-mark fan-out: Tone right, Capability wrong, take one.
 *
 * Polling, not streaming
 * ----------------------
 * Deliberate. A dropped socket must not be the difference between a visible chain and a
 * silent one, and the run's own call rows are the source of truth either way. Streaming
 * over `WS /api/v1/ws/execution` is an optimisation available later.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Tag, Tooltip, message } from "antd";
import { AlertTriangle, Check, GitBranch, Loader2, RefreshCw, Undo2, X } from "lucide-react";

import UnifiedDiffViewer from "@/components/shared/UnifiedDiffViewer";
import { applyKernelPatch } from "@/services/api/kernelBotApi";
import {
  type KernelMemory,
  type KernelProposal,
  type PatchRunProposals,
  getKernelMemory,
  getPatchRunProposals,
  recordProposalVerdict,
  resumePatchRun,
  revertAutoLearnPatch,
} from "@/services/api/spanFeedbackApi";

interface KernelFixRunPanelProps {
  /** Run id from /compose-version. Null when no background run was started. */
  patchRunId?: string | null;
  entityId?: string;
}

/** How often to re-read while the run still looks unfinished. */
const POLL_MS = 6000;
/**
 * Stop polling eventually. `73` is `gemini-3.1-pro` at HIGH and the cap is 5 marks, so a
 * run that has produced nothing after this long has failed in a way polling won't fix —
 * and a tab left open should not poll forever.
 */
const MAX_POLLS = 40;

export default function KernelFixRunPanel({ patchRunId, entityId }: KernelFixRunPanelProps) {
  const [run, setRun] = useState<PatchRunProposals | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  const [diffOf, setDiffOf] = useState<KernelProposal | null>(null);
  const [deciding, setDeciding] = useState<string | null>(null);
  // D-029 — which proposal is mid-revert. Separate from `applying` so the two buttons
  // cannot spin together on one row.
  const [reverting, setReverting] = useState<string | null>(null);
  const pollCount = useRef(0);

  // IN-10 — "what we learned", fetched on demand and cached per kernel. The toast says the
  // preference was acknowledged; this is the durable half that is still there tomorrow.
  const [memoryOpen, setMemoryOpen] = useState<string | null>(null);
  const [memoryByKernel, setMemoryByKernel] = useState<Record<string, KernelMemory>>({});
  const [memoryLoading, setMemoryLoading] = useState<string | null>(null);

  const toggleMemory = useCallback(
    async (kernelKey: string) => {
      if (memoryOpen === kernelKey) {
        setMemoryOpen(null);
        return;
      }
      setMemoryOpen(kernelKey);
      if (memoryByKernel[kernelKey]) return; // already read once — do not re-fetch on a poll
      setMemoryLoading(kernelKey);
      try {
        const res = await getKernelMemory(kernelKey, entityId);
        if (res.status && res.data) {
          setMemoryByKernel((prev) => ({ ...prev, [kernelKey]: res.data as KernelMemory }));
        }
      } catch {
        // Swallowed on purpose: an unreachable memory read must not replace the run's real
        // state with an error banner.
      } finally {
        setMemoryLoading(null);
      }
    },
    [memoryOpen, memoryByKernel, entityId],
  );

  const load = useCallback(async () => {
    if (!patchRunId) return;
    setLoading(true);
    try {
      const res = await getPatchRunProposals({ patchRunId, entityId });
      if (res.status && res.data) setRun(res.data);
    } catch {
      // Swallowed on purpose: this polls, and a transient read failure should not
      // replace a run's real state with an error banner. A run that never appears
      // shows as "still running", which is what the next poll will correct.
    } finally {
      setLoading(false);
    }
  }, [patchRunId, entityId]);

  useEffect(() => {
    pollCount.current = 0;
    setRun(null);
    setDiffOf(null);
    if (!patchRunId) return;

    void load();
    const timer = setInterval(() => {
      pollCount.current += 1;
      if (pollCount.current > MAX_POLLS) {
        clearInterval(timer);
        return;
      }
      void load();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [patchRunId, load]);

  const handleApply = useCallback(
    async (proposal: KernelProposal, allowSharedEdit = false) => {
      if (!proposal.kernelKey || !proposal.proposedContent) return;
      setApplying(proposal.callId);
      try {
        const res = await applyKernelPatch(
          proposal.kernelKey,
          proposal.proposedContent,
          `PROJECT-111A marked feedback — ${proposal.exchangeId ?? "exchange"}`,
          allowSharedEdit,
          // The same run id, so the applied version joins the run it came from.
          patchRunId ?? undefined,
        );

        if (res.statusCode === 409) {
          // Not a failure — a question. Ask it, with the blast radius named.
          const blocked = res.data as { bot_count?: number; why?: string } | null;
          Modal.confirm({
            title: `${proposal.kernelKey} is shared`,
            content: (
              <div className="text-xs space-y-2">
                <div>
                  This kernel is assembled into {blocked?.bot_count ?? "several"} bots.
                  Applying changes all of them.
                </div>
                {blocked?.why && (
                  <div className="text-muted-foreground">{blocked.why}</div>
                )}
              </div>
            ),
            okText: "Apply to all of them",
            cancelText: "Cancel",
            onOk: () => handleApply(proposal, true),
          });
          return;
        }

        if (res.status) {
          message.success(`${proposal.kernelKey} updated.`);
          void load();
        } else {
          message.warning(res.message || "Could not apply this change");
        }
      } catch (e) {
        message.error(`The change wasn't applied: ${e}`);
      } finally {
        setApplying(null);
      }
    },
    [patchRunId, load],
  );

  /**
   * D-029 — undo an applied change.
   *
   * Confirmed before firing, deliberately: this writes a new kernel version, so a misclick
   * costs another version on a kernel that may be shared by several bots. The undo is
   * itself undoable (nothing is deleted), but that is a reason to be honest in the prompt,
   * not a reason to skip it.
   */
  const handleRevert = useCallback(
    async (proposal: KernelProposal) => {
      if (!proposal.kernelKey || !patchRunId) return;
      Modal.confirm({
        title: `Revert ${proposal.kernelKey}?`,
        content: (
          <div className="text-xs space-y-2">
            <div>
              Restores the version from before this change. The reverted version is kept —
              the restore is written as a NEW version, so nothing is lost either way.
            </div>
            <div className="text-muted-foreground">
              Refused if the kernel has been edited since, because reverting would discard
              that later change too.
            </div>
          </div>
        ),
        okText: "Revert it",
        okButtonProps: { danger: true },
        cancelText: "Keep the change",
        onOk: async () => {
          setReverting(proposal.callId);
          try {
            const res = await revertAutoLearnPatch({
              kernelKey: proposal.kernelKey!,
              patchRunId,
              entityId,
            });
            if (res.status) {
              message.success(`${proposal.kernelKey} reverted.`);
              void load();
            } else {
              // 409 is a real answer — the kernel moved on, or there is nothing to undo.
              message.warning(res.message || "Could not revert this change");
            }
          } catch (e) {
            message.error(`The revert didn't complete: ${e}`);
          } finally {
            setReverting(null);
          }
        },
      });
    },
    [patchRunId, entityId, load],
  );

  // T0842 — rejecting is a RECORDED decision, not a dismissal. Without it "reviewed and
  // rejected" was indistinguishable from "never opened", and 73 could re-propose the same
  // edit next run with nothing saying a human had already refused it.
  const handleReject = useCallback(
    async (proposal: KernelProposal) => {
      setDeciding(proposal.callId);
      try {
        // D-029 — if auto-learn has ALREADY applied this change, rejecting has to UNDO it.
        // Before this, `Reject` recorded the verdict and left the change live: the user
        // said no and the kernel kept it, which is exactly the "reviewed and rejected"
        // state T0842 exists to make impossible. The verdict is recorded either way.
        if (proposal.applied && patchRunId && proposal.kernelKey) {
          const undone = await revertAutoLearnPatch({
            kernelKey: proposal.kernelKey,
            patchRunId,
            entityId,
          });
          if (!undone.status) {
            // Never claim a rejection landed when the undo did not.
            message.warning(undone.message || "Could not undo the applied change");
            return;
          }
        }

        const res = await recordProposalVerdict({
          patchCallId: proposal.callId,
          verdict: "rejected",
          patchRunId: patchRunId ?? undefined,
          kernelKey: proposal.kernelKey ?? undefined,
          entityId,
        });
        if (res.status) {
          message.success(
            proposal.applied
              ? "Rejected — the change was undone and recorded."
              : "Rejected — recorded, nothing was applied.",
          );
          void load();
        } else {
          message.warning(res.message || "Could not record the rejection");
        }
      } catch (e) {
        message.error(`Could not record the rejection: ${e}`);
      } finally {
        setDeciding(null);
      }
    },
    [patchRunId, entityId, load],
  );

  const handleResume = useCallback(async () => {
    if (!patchRunId) return;
    setResuming(true);
    try {
      const res = await resumePatchRun({ patchRunId, entityId });
      if (res.status && res.data) {
        message.success(
          res.data.queued
            ? `Resuming ${res.data.queued} mark(s).`
            : "Nothing left to resume.",
        );
        pollCount.current = 0;
        void load();
      } else {
        message.warning(res.message || "Could not resume this run");
      }
    } catch (e) {
      message.error(`Resuming didn't complete: ${e}`);
    } finally {
      setResuming(false);
    }
  }, [patchRunId, entityId, load]);

  if (!patchRunId) return null;

  const proposals = run?.proposals ?? [];
  const refusals = run?.refusals ?? [];
  // T0810 — the BE compares marks-the-run-was-started-for against marks actually routed.
  // `complete: null` means it could not tell; treat that as "still working" rather than
  // asserting either answer.
  const incomplete = run?.complete === false;
  const stillRunning = pollCount.current <= MAX_POLLS && (!run || run.complete !== true);
  // Offer resume only once polling has given up. Before that, "incomplete" almost always
  // just means "still running", and a resume button there invites duplicate work.
  const canResume = incomplete && pollCount.current > MAX_POLLS;

  return (
    <div className="space-y-2 pb-3 border-b border-border/40 dark:border-gray-800">
      <div className="flex items-center gap-1.5">
        <GitBranch className="h-3.5 w-3.5 text-violet-500" />
        <span className="text-label-2xs uppercase tracking-widest font-semibold text-muted-foreground flex-1">
          Kernel fix run
        </span>
        {(loading || stillRunning) && (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground/70" />
        )}
        <Tooltip title="Re-read this run">
          <Button
            size="small"
            type="text"
            icon={<RefreshCw className="h-3 w-3" />}
            onClick={() => void load()}
          />
        </Tooltip>
      </div>

      {/* 🔁 T1414 INDEPENDENT READ — the hardcoded `62 → 73` used to sit here, rendered for
          EVERY run whether or not the router had done anything. It was caught by a reader who
          opened this run and saw `routerCalls: []` beside it: the header advertised a two-stage
          chain the payload did not evidence. The chain is a real description of the FEATURE (it
          stays on the page header and in `MarkableOutput`'s "Kernel fix running"), but a RUN's
          header may only state what that run's own numbers show. */}
      <div className="text-label-2xs text-muted-foreground/70 leading-snug">
        <span className="font-mono">{patchRunId}</span>
        {run ? (
          <>
            {" "}·{" "}
            {run.expectedMarkCount === null
              ? `${run.routedMarkCount} mark(s) routed (the expected count was not recorded)`
              : `${run.routedMarkCount} of ${run.expectedMarkCount} mark(s) routed`}
            {" "}· {run.proposalCount} proposal(s), {run.appliedCount} applied
          </>
        ) : (
          " · starting…"
        )}
      </div>

      {/* D-029 — auto-learn applies the patch WHEN the posture allows it; the reviewer's job is
          then to REVERT rather than to commit. The posture is read from the run instead of
          asserted, because an independent read of this panel caught it claiming changes "are
          applied automatically" on a run showing `0 applied` with an untouched Apply button —
          while the page header said "Nothing here is in force". Two sentences, one screen,
          opposite meanings. */}
      <div className="text-label-2xs text-muted-foreground/70 leading-snug">
        {run && run.appliedCount > 0
          ? `${run.appliedCount} change(s) applied automatically and reversible — revert any you disagree with.`
          : "Nothing here is in force: each change waits for someone to apply it, and applying is reversible."}
      </div>

      {/* T0810 — a run that died must not read as a run that found nothing. */}
      {incomplete && (
        <div className="rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/20 px-2 py-1.5 space-y-1">
          <div className="flex items-start gap-1.5">
            <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
            <span className="text-label-2xs text-amber-800 dark:text-amber-400 leading-snug flex-1">
              {run?.livenessNote}
            </span>
          </div>
          {canResume && (
            <Button size="small" loading={resuming} onClick={() => void handleResume()} block>
              Finish the remaining {(run?.expectedMarkCount ?? 0) - (run?.routedMarkCount ?? 0)} mark(s)
            </Button>
          )}
        </div>
      )}

      {!proposals.length && (
        <div className="text-label-2xs text-subtle-foreground">
          {stillRunning
            ? "Diagnosing the kernels behind your marks…"
            : incomplete
              ? "This run stopped before it finished."
              : "This run proposed no kernel changes."}
        </div>
      )}

      {/* D-135-022 — a run that refused every mark and a run that died both show an empty
          proposal list. This is the difference, said out loud: the chain recorded WHY it
          proposed nothing, in words written for a person. */}
      {!!refusals.length && (
        <div className="rounded border border-border/40 dark:border-gray-800 px-2 py-1.5 space-y-1">
          <div className="text-label-2xs uppercase tracking-widest font-semibold text-muted-foreground">
            Looked at, nothing changed ({refusals.length})
          </div>
          {refusals.map((r, i) => (
            <div
              // 🚨 `exchangeId` IS NOT UNIQUE HERE, AND KEYING ON IT LOGGED A REACT ERROR.
              //
              // Found by FE QA on 2026-09-15: `key={r.exchangeId ?? r.reasonUser ?? …}` produced
              // "Encountered two children with the same key … 55f7ce5c-6b81-4883-be94-e71ac5a19a39"
              // four times on every render. Verified against the BE payload rather than guessed:
              // that run's `refusals` holds THREE entries with the SAME `exchangeId`, because
              // several marks can sit on ONE exchange row and `_persist_refusals` writes one
              // refusal per ROW. The `?? reasonUser` fallback then collapses every one of them onto
              // the same sentence — so both the id and the fallback repeat.
              //
              // React's own warning names the stakes: non-unique keys may cause children to be
              // DUPLICATED or OMITTED. Duplicated refusals would overstate how many marks were
              // looked at, which is exactly the number this block exists to report honestly — it
              // is an accuracy bug wearing a lint warning's clothes.
              //
              // The index is the correct discriminator: these rows are positional renderings of one
              // exchange's refusals, carrying no per-mark identity of their own. ⚠️ If the payload
              // later gains a per-mark id (the spans themselves would separate them), key on THAT
              // instead — an index is stable here only because nothing reorders within a run.
              key={`${r.exchangeId ?? r.reasonUser ?? r.reason ?? "refusal"}-${i}`}
              className="text-label-2xs text-muted-foreground/80 leading-snug"
              title={r.reason ?? r.timestamp ?? undefined}
            >
              · {r.reasonUser}
            </div>
          ))}
        </div>
      )}

      {proposals.map((p) => (
        <div
          key={p.callId}
          className="rounded border border-border/40 dark:border-gray-800 px-2 py-1.5 space-y-1"
        >
          {/* D-135-025 — WHAT WE LEARNED, IN WORDS, above everything technical.
              👤: *"we cant just say this … but the actual information or actual learning must
              be showed"*. The kernel key and the version bump below are how the MACHINE records
              the change; this sentence is how the PERSON reads it, and it is placed first
              because it is the only line here written for them.

              🚨 THREE STATES, NEVER TWO — and the FTUE persona is why:
                * tagging rule 1: *"The machine never says it did nothing."*
                * rule 2: *"Learning must be visible or it is indistinguishable from failure"*.
              Rendering nothing when `ruleLearned` is absent therefore FAILS the persona — which is
              exactly what the first version of this block did.

              And the two absences are NOT the same fact, so they do not get the same sentence:
                * `ruleOmitted` — the patcher ANSWERED and judged the change to be plumbing. We can
                  say that, because it did say it.
                * neither — we have no answer (an older row, or a contract not yet applied). We
                  report only what exists and never attribute a judgement we were not given. */}
          {p.ruleLearned ? (
            <div className="rounded bg-violet-50/60 dark:bg-violet-950/25 border border-violet-200/70 dark:border-violet-800/50 px-2 py-1">
              <span className="text-label-2xs uppercase tracking-widest text-violet-700 dark:text-violet-300 font-semibold">
                What we learned
              </span>
              <div className="text-label-xs text-foreground/90 leading-snug">{p.ruleLearned}</div>
            </div>
          ) : (
            <div className="text-label-2xs text-muted-foreground/80 leading-snug">
              {p.ruleOmitted
                ? "Applied. This change was mechanical — there is no rule a person could restate from it."
                : "Change recorded. No plain-language rule was written for this one."}
            </div>
          )}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-label-xs font-mono break-all flex-1">
              {p.kernelKey ?? "unresolved"}
            </span>
            {p.currentVersion != null && (
              <span className="text-label-2xs text-muted-foreground/70">
                v{p.currentVersion} → v{p.currentVersion + 1}
              </span>
            )}
            {p.applied && <Tag color="green">applied</Tag>}
            {/* IN-21 — the kernel's accept/reject ratio, so the user sees how confident
                the system's own track record is. `null` = no history, not 0%. */}
            {p.confidenceScore != null && (
              <Tooltip title={`${p.approvals ?? 0} accepted · ${p.rejections ?? 0} rejected — every verdict on this rule`}>
                <Tag color={p.confidenceScore >= 50 ? "blue" : "orange"}>
                  {p.confidenceScore}% accepted by past verdicts
                </Tag>
              </Tooltip>
            )}
            {/* T0842 — the state that previously could not exist. */}
            {!p.applied && p.verdict === "rejected" && (
              <Tooltip title={p.verdictReason || `Rejected by ${p.decidedBy ?? "a reviewer"}`}>
                <Tag color="red">rejected</Tag>
              </Tooltip>
            )}
          </div>

          {p.kernelName && (
            <div className="text-label-2xs text-muted-foreground/70">{p.kernelName}</div>
          )}

          {/* IN-10 — the durable half of "we learned". The toast says the preference was
              acknowledged; this is the memory still on the kernel tomorrow, which is what
              "we won't forget" has to mean to be an honest claim. */}
          {p.kernelKey && (
            <div className="space-y-1">
              <button
                type="button"
                className="text-label-2xs text-violet-600 dark:text-violet-400 hover:underline"
                onClick={() => void toggleMemory(p.kernelKey!)}
              >
                {memoryOpen === p.kernelKey ? "▾" : "▸"} What we've learned on this rule
                {memoryByKernel[p.kernelKey] ? ` (${memoryByKernel[p.kernelKey].count})` : ""}
              </button>
              {memoryOpen === p.kernelKey && (
                <div className="pl-3 space-y-0.5">
                  {memoryLoading === p.kernelKey && (
                    <div className="text-label-2xs text-subtle-foreground">Reading…</div>
                  )}
                  {memoryByKernel[p.kernelKey]?.memoryLines.length ? (
                    memoryByKernel[p.kernelKey].memoryLines.map((line) => (
                      <div
                        key={line.exchangeId ?? line.text}
                        className="text-label-2xs text-muted-foreground/80 leading-snug"
                        title={line.writtenAt ?? undefined}
                      >
                        · {line.text}
                      </div>
                    ))
                  ) : (
                    memoryLoading !== p.kernelKey && (
                      <div className="text-label-2xs text-subtle-foreground leading-snug">
                        No memory line is stored on this rule yet. This list is the STORE, not
                        this run: the takeaway above is this run's own answer and appears here
                        once it has been written.
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-label-2xs text-muted-foreground/70">
              {p.appliedOps.length} op(s)
            </span>
            {/* T0846 — one proposal can now answer several marks. Saying so matters:
                the reviewer needs to know accepting this covers all of them, because
                before grouping these were rival proposals and applying one dropped
                the others' corrections without saying so. */}
            {(p.exchangeIds?.length ?? 0) > 1 && (
              <Tooltip title={p.exchangeIds!.join("\n")}>
                <span className="text-label-2xs text-violet-600 dark:text-violet-400 cursor-help">
                  covers {p.exchangeIds!.length} corrections
                </span>
              </Tooltip>
            )}
            {/* Never hidden: a proposal rebuilt from partly-stale ops is not the
                proposal the patcher wrote, and the reviewer has to know that. */}
            {!!p.skippedOps.length && (
              <Tooltip
                title={p.skippedOps.map((o) => `#${o.index} ${o.op}: ${o.error}`).join("\n")}
              >
                <span className="inline-flex items-center gap-1 text-label-2xs text-amber-700 dark:text-amber-500 cursor-help">
                  <AlertTriangle className="h-3 w-3" />
                  {p.skippedOps.length} no longer apply
                </span>
              </Tooltip>
            )}
            {p.scope && <span className="text-label-2xs text-subtle-foreground">{p.scope}</span>}
          </div>

          {p.reason && (
            <div className="text-label-2xs text-muted-foreground/70 leading-snug">{p.reason}</div>
          )}

          {p.reviewable === false && (
            <div className="text-label-2xs text-muted-foreground/70">
              Every op is stale against the current version — nothing left to apply.
            </div>
          )}

          {p.proposedContent && p.reviewable !== false && (
            <div className="flex items-center gap-2 pt-0.5">
              <Button size="small" onClick={() => setDiffOf(p)} block>
                See the change
              </Button>
              {/* T0843 — `canApply === false` means the BE has already decided this
                  viewer cannot commit it (global kernel + non-admin, or no `editor` on
                  the entity). Say so instead of offering a button that 403s. `undefined`
                  = a backend predating the field, so the button stays. */}
              {!p.applied && p.canApply === false && (
                <Tooltip title="You need editor permission on this kernel's entity — global kernels are admin-only.">
                  <span className="text-label-2xs text-muted-foreground/60 cursor-help flex-1 text-center">
                    Review only — not yours to apply
                  </span>
                </Tooltip>
              )}
              {/* 🚨 D-029 — THE UNDO FOR AUTO-LEARN, AND THE REASON AUTO-APPLY IS
                  DEFENSIBLE AT ALL. Auto-learn commits `73`'s patch to every kernel,
                  shared ones included, so nothing reviews the write before it lands.
                  Reversibility stands in for review — the persona asks for "every action
                  auditable and reversible", not for pre-approval — which makes this
                  button load-bearing rather than a convenience.
                  Shown on ANY applied proposal, not only auto-applied ones: a human
                  who clicked Apply and changed their mind needs the same undo, and the
                  restore is identical either way. */}
              {p.applied && p.kernelKey && p.canApply !== false && (
                <Tooltip title="Restore the version from before this change. Nothing is deleted — the restore is a new version.">
                  <Button
                    size="small"
                    danger
                    icon={<Undo2 className="h-3.5 w-3.5" />}
                    loading={reverting === p.callId}
                    onClick={() => handleRevert(p)}
                    block
                  >
                    Revert
                  </Button>
                </Tooltip>
              )}
              {!p.applied && p.canApply !== false && p.verdict !== "rejected" && (
                <>
                  <Button
                    size="small"
                    type="primary"
                    icon={<Check className="h-3.5 w-3.5" />}
                    loading={applying === p.callId}
                    onClick={() => handleApply(p)}
                    block
                  >
                    Apply
                  </Button>
                  {/* T0842 — the other verdict. Recorded, never a silent dismissal. */}
                  <Button
                    size="small"
                    danger
                    icon={<X className="h-3.5 w-3.5" />}
                    loading={deciding === p.callId}
                    onClick={() => void handleReject(p)}
                    block
                  >
                    Reject
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      ))}

      {/* D-016: the kernel version differ, reused. No diff code lives in this file. */}
      <Modal
        open={!!diffOf}
        onCancel={() => setDiffOf(null)}
        footer={null}
        width="80vw"
        title={
          <span className="text-sm font-mono">
            {diffOf?.kernelKey} · v{diffOf?.currentVersion} → proposed
          </span>
        }
      >
        {diffOf && (
          <UnifiedDiffViewer
            oldValue={diffOf.currentContent ?? ""}
            newValue={diffOf.proposedContent ?? ""}
            leftTitle={`v${diffOf.currentVersion} (in force)`}
            rightTitle="Proposed — not applied"
          />
        )}
      </Modal>
    </div>
  );
}
